import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { isChunkLoadError, shouldReloadForChunkError } from '@/core/error-handling';
import { logger } from '@/core/logging/logger';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';

/** The Under Maintenance page, a static file in `public/` so it shows without any of the app's code. */
const MAINTENANCE_PAGE = '/maintenance.html';

/**
 * What the router shows when a route throws -- in place of React Router's built-in "Unexpected Application
 * Error!" page, which prints the raw error and its stack.
 *
 * A route throws mostly when its lazy page chunk fails to download: the tab is running a build that has since
 * been redeployed, and the chunk it names is gone (see `chunkLoadError.ts`). That case reloads the page once,
 * which picks up the current build. If the chunk still fails within the reload window, a deployment is still
 * under way, so the user is sent to the Under Maintenance page, which brings them back to this URL once the app
 * answers again. Anything else gets a plain screen with a Reload button.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const chunkFailed = isChunkLoadError(error);
  const reloading = shouldReloadForChunkError(error);

  useEffect(() => {
    if (reloading) {
      logger.warn('A page chunk failed to load; reloading for the current build', error);
      window.location.reload();
    } else if (chunkFailed) {
      logger.warn('A page chunk failed to load again after a reload; showing the maintenance page', error);
      const { pathname, search, hash } = window.location;
      window.location.replace(`${MAINTENANCE_PAGE}?from=${encodeURIComponent(pathname + search + hash)}`);
    } else {
      logger.error('Route error', error);
    }
  }, [error, reloading, chunkFailed]);

  if (reloading || chunkFailed) return <LoadingSpinner fullPage label="Updating to the latest version..." />;

  return (
    <div className="route-error" role="alert">
      <h1 className="route-error__title">Something went wrong</h1>
      <p className="route-error__message">
        An unexpected error stopped this page. Reload to try again; if it keeps happening, contact your administrator.
      </p>
      <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
        <span className="btn__label">Reload</span>
      </button>
    </div>
  );
}
